import { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useForm, type UseFormRegister } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { Sparkles, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import StepShell from '@/components/form/StepShell'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/auth-client'
import { useProviderProfile } from '@/hooks/useProviderProfile'
import { useCreateProvider } from '@/hooks/useProviderMutations'

const schema = z.object({
  bio: z.string().max(500).optional().or(z.literal('')),
  location: z.object({
    city: z.string().trim().min(1, 'City is required'),
    region: z.string().trim().min(1, 'Province is required'),
  }),
})

type ProfileForm = z.infer<typeof schema>

const STEPS = ['welcome', 'bio', 'location', 'done'] as const

const inputClass = (hasError?: boolean) =>
  cn(
    'w-full px-4 py-3 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50',
    hasError ? 'border-destructive focus-visible:border-destructive' : 'border-input focus-visible:border-ring'
  )

export default function ProviderOnboarding() {
  const navigate = useNavigate()
  const { data: session, isPending: sessionPending } = useSession()
  const { data: profileData, isLoading: profileLoading } = useProviderProfile()
  const createProvider = useCreateProvider()

  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState(1)

  const {
    register,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<ProfileForm>({
    resolver: zodResolver(schema),
    defaultValues: { bio: '', location: { city: '', region: '' } },
    mode: 'onTouched',
  })

  if (sessionPending) return <FullScreenLoader />
  if (!session) return <Navigate to="/login" replace />
  if (profileLoading) return <FullScreenLoader />
  if (profileData?.provider) return <Navigate to="/provider" replace />

  const goTo = (next: number) => {
    setDirection(next > step ? 1 : -1)
    setStep(next)
  }

  const handleContinue = async () => {
    const name = STEPS[step]
    if (name === 'welcome') return goTo(1)
    if (name === 'bio') return goTo(2)
    if (name === 'location') {
      if (!(await trigger('location'))) return
      const data = getValues()
      try {
        await createProvider.mutateAsync({
          bio: data.bio?.trim() || undefined,
          location: { city: data.location.city.trim(), region: data.location.region.trim() },
        })
        goTo(3)
      } catch {
        toast.error("Couldn't create your profile. Please try again.")
      }
    }
  }

  return (
    <StepShell
      currentStep={step}
      totalSteps={STEPS.length}
      stepKey={STEPS[step]}
      direction={direction}
      onBack={step > 0 && step < 3 ? () => goTo(step - 1) : undefined}
      onContinue={step < 3 ? handleContinue : undefined}
      continueLabel={
        createProvider.isPending
          ? 'Creating…'
          : step === 0
            ? "Let's go"
            : step === 2
              ? 'Finish'
              : 'Continue'
      }
      continueDisabled={createProvider.isPending}
      hideControls={step === 3}
    >
      {STEPS[step] === 'welcome' && <WelcomeStep />}
      {STEPS[step] === 'bio' && <BioStep register={register} />}
      {STEPS[step] === 'location' && <LocationStep register={register} errors={errors} />}
      {STEPS[step] === 'done' && <DoneStep navigate={navigate} />}
    </StepShell>
  )
}

function FullScreenLoader() {
  return <div className="min-h-screen bg-background" />
}

function WelcomeStep() {
  return (
    <div className="text-center">
      <div className="w-16 h-16 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mx-auto mb-6">
        <Sparkles className="size-8" />
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Let's set up your provider profile</h1>
      <p className="text-lg text-muted-foreground max-w-md mx-auto">
        This only takes a minute. You can always update it later.
      </p>
    </div>
  )
}

function BioStep({ register }: { register: UseFormRegister<ProfileForm> }) {
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Tell customers about yourself</h1>
      <p className="text-muted-foreground mb-6">
        A short bio helps build trust. You can skip this for now.
      </p>
      <textarea
        {...register('bio')}
        rows={4}
        maxLength={500}
        placeholder="I've been helping people in the Montréal area for over 5 years…"
        className={cn(inputClass(), 'resize-none')}
      />
    </div>
  )
}

function LocationStep({
  register,
  errors,
}: {
  register: UseFormRegister<ProfileForm>
  errors: ReturnType<typeof useForm<ProfileForm>>['formState']['errors']
}) {
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Where are you based?</h1>
      <p className="text-muted-foreground mb-6">
        This helps customers find services in their area.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input
            {...register('location.city')}
            placeholder="City"
            className={inputClass(!!errors.location?.city)}
          />
          {errors.location?.city && (
            <p className="text-destructive text-sm mt-1">{errors.location.city.message}</p>
          )}
        </div>
        <div>
          <input
            {...register('location.region')}
            placeholder="Province"
            className={inputClass(!!errors.location?.region)}
          />
          {errors.location?.region && (
            <p className="text-destructive text-sm mt-1">{errors.location.region.message}</p>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-3">Currently available across Canada 🇨🇦</p>
    </div>
  )
}

function DoneStep({ navigate }: { navigate: (path: string) => void }) {
  return (
    <div className="text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
        className="w-16 h-16 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto mb-6"
      >
        <CheckCircle2 className="size-10" />
      </motion.div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">You're all set!</h1>
      <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
        Your provider profile is live. Add your first service to start getting bookings.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button size="lg" onClick={() => navigate('/provider/services/new')}>
          Add your first service
        </Button>
        <Button size="lg" variant="outline" onClick={() => navigate('/provider')}>
          Go to dashboard
        </Button>
      </div>
    </div>
  )
}
